/**
 * Quantity ↔ base-unit arithmetic, mirroring `private.to_base_qty`,
 * `private.require_item_factor` and `public.convert_item_qty`
 * (migration 20260923000002) line for line.
 *
 * Why it is mirrored rather than left to the server (decision C17): a quantity
 * whose base amount is not representable at the base unit's precision is
 * rejected by the posting engine, and the form must say so *before* the entry is
 * queued — an offline movement that only fails on sync is the worst outcome.
 * 12,3 koli of a pcs-based napkin is 9.840 adet and fine; 0,001 koli is 0,8 adet
 * and refused.
 */
import { D, roundTo, QTY_SCALE, type Dec, type DecimalInput } from '../money';

/** One `item_uoms` row, as PostgREST delivers it (numerics arrive as numbers). */
export interface ItemUomFactor {
  unit: string;
  factor_to_base: number | string | null;
}

/** Everything the rule needs about one item; `uoms` are its `item_uoms` rows. */
export interface ItemUomContext {
  baseUnit: string;
  uoms: ItemUomFactor[];
  /**
   * `units.precision` of the base unit. The SQL takes `least(precision, 3)`, so
   * anything higher is clamped here too. Missing means 3, as in `coalesce(…, 3)`.
   */
  basePrecision?: number | null;
  /** `units.name_tr` of the base unit, used in the error message ("adet"). */
  baseUnitLabel?: string | null;
}

/**
 * The first three mirror the hints `private.to_base_qty` raises, so a screen can
 * key off one constant whichever side refused. `qty_invalid` is client-only: the
 * server never sees a quantity that is not a number, because the form's
 * `decimalText` schema stops it first.
 */
export type QtyErrorHint = 'qty_positive' | 'unit_unknown' | 'qty_not_integral' | 'qty_invalid';

/**
 * Carries the same `hint` the database raises, so a screen can key off one
 * constant whether the refusal came from here or from the server.
 */
export class QtyError extends Error {
  constructor(
    message: string,
    public readonly hint: QtyErrorHint,
    public readonly unit?: string,
  ) {
    super(message);
    this.name = 'QtyError';
  }
}

/**
 * Quantities reach here already parsed — Turkish-formatted text goes through
 * `parseNumber()` in the form, as every Phase 1 amount does. Garbage would make
 * `D()` throw a `DecimalError`, which a form's validator has no way to read, so
 * it is turned into a `QtyError` like every other refusal.
 */
function decimal(qty: DecimalInput, unit?: string): Dec {
  try {
    return D(qty);
  } catch {
    throw new QtyError('Geçerli bir sayı gir (ör. 1.234,56)', 'qty_invalid', unit);
  }
}

/** `least(precision, 3)` with the SQL's `coalesce(…, 3)` default. */
function basePrecision(ctx: ItemUomContext): number {
  const p = ctx.basePrecision;
  if (p === null || p === undefined || !Number.isFinite(p)) return QTY_SCALE;
  return Math.min(Math.max(Math.trunc(p), 0), QTY_SCALE);
}

/** How the base unit is named in messages — its Turkish name, else its code. */
function baseLabel(ctx: ItemUomContext): string {
  return ctx.baseUnitLabel?.trim() || ctx.baseUnit;
}

/**
 * The item's own factor for `unit`, or null when the unit is not one of its
 * UoMs. The base unit is always 1 even when no row spells it out — the SQL
 * forces `factor_to_base = 1` on the base row, and `save_item` creates it.
 */
export function itemUnitFactor(ctx: ItemUomContext, unit: string | null | undefined): Dec | null {
  const u = unit ?? ctx.baseUnit;
  if (u === ctx.baseUnit) return D(1);
  const row = ctx.uoms.find((r) => r.unit === u);
  if (!row || row.factor_to_base === null || row.factor_to_base === undefined) return null;
  const f = D(row.factor_to_base);
  return f.greaterThan(0) ? f : null;
}

/** Same as `itemUnitFactor`, but refuses like `private.require_item_factor`. */
export function requireItemFactor(ctx: ItemUomContext, unit: string | null | undefined): Dec {
  const f = itemUnitFactor(ctx, unit);
  if (!f) throw new QtyError(`Birim bu ürün için tanımlı değil: ${unit ?? ctx.baseUnit}`, 'unit_unknown', unit ?? ctx.baseUnit);
  return f;
}

/**
 * Entered quantity → base quantity. Throws `QtyError` exactly where
 * `private.to_base_qty` raises: a quantity that is not above zero, a unit the
 * item does not have, and a base amount that does not land on the base unit's
 * precision.
 */
export function toBaseQty(qty: DecimalInput, unit: string | null | undefined, ctx: ItemUomContext): Dec {
  const q = decimal(qty, unit ?? ctx.baseUnit);
  if (q.lessThanOrEqualTo(0)) throw new QtyError('Miktar sıfırdan büyük olmalı', 'qty_positive', unit ?? ctx.baseUnit);
  const base = q.times(requireItemFactor(ctx, unit));
  const prec = basePrecision(ctx);
  if (!base.equals(roundTo(base, prec))) {
    throw new QtyError(`Miktar temel birimde (${baseLabel(ctx)}) tam sayı olmuyor`, 'qty_not_integral', unit ?? ctx.baseUnit);
  }
  return roundTo(base, QTY_SCALE);
}

export type BaseQtyCheck = { ok: true; qtyBase: Dec } | { ok: false; hint: QtyErrorHint; message: string };

/**
 * `toBaseQty` without the throw — what a form's validator and the mobile keypad
 * call on every keystroke.
 */
export function checkBaseQty(qty: DecimalInput, unit: string | null | undefined, ctx: ItemUomContext): BaseQtyCheck {
  try {
    return { ok: true, qtyBase: toBaseQty(qty, unit, ctx) };
  } catch (e) {
    if (e instanceof QtyError) return { ok: false, hint: e.hint, message: e.message };
    throw e;
  }
}

/**
 * Base quantity → `unit`, rounded to 6 decimals the way `convert_item_qty` does.
 * Display rounding is the caller's job (`formatQty`).
 */
export function fromBaseQty(qtyBase: DecimalInput, unit: string | null | undefined, ctx: ItemUomContext): Dec {
  return roundTo(D(qtyBase).dividedBy(requireItemFactor(ctx, unit)), 6);
}

/**
 * Item-aware unit conversion — the client-side twin of `convert_item_qty`.
 * Named apart from `convertQty` in `units.ts`, which converts with the company
 * packaging spec rather than the item's own UoM rows.
 */
export function convertItemQty(
  qty: DecimalInput,
  fromUnit: string | null | undefined,
  toUnit: string | null | undefined,
  ctx: ItemUomContext,
): Dec {
  return roundTo(D(qty).times(requireItemFactor(ctx, fromUnit)).dividedBy(requireItemFactor(ctx, toUnit)), 6);
}

/**
 * How many decimals may be typed in `unit` so that **every** such quantity is
 * representable at the base unit's precision — the rule the mobile keypad's
 * decimal key follows (C18).
 *
 * A quantity with d decimals is k ÷ 10^d, so its base amount is k × F ÷ 10^d and
 * lands on the precision p for every k exactly when F × 10^(p − d) is an integer.
 *
 * Returns 0 for a unit no decimal count works for (a kg row on a pcs-based
 * napkin, whose factor is 120,048019): such a unit is not enterable at all, and
 * `checkBaseQty` is what says so.
 */
export function maxQtyDecimals(unit: string | null | undefined, ctx: ItemUomContext): number {
  const factor = itemUnitFactor(ctx, unit);
  if (!factor) return 0;
  const p = basePrecision(ctx);
  for (let d = QTY_SCALE; d > 0; d--) {
    if (factor.times(D(10).pow(p - d)).isInteger()) return d;
  }
  return 0;
}
